import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchAiSpeech } from './aiChatService'
import { splitForSpeech } from './replyText'

// Read aloud: one reply at a time. Speech comes from the server's local voices
// (Kokoro for English/Hindi, AI4Bharat Indic for Tamil etc.) a few sentences
// at a time - the next piece is fetched while the current one plays. When the
// server can't speak (not set up, no voice for the language, error), the rest
// is read by the browser's own voice instead.

const browserVoice = () =>
    typeof window !== 'undefined' && 'speechSynthesis' in window

const aborted = () => new DOMException('Aborted', 'AbortError')

/** Plays one audio blob; resolves when it ends, rejects if stopped. */
const playBlob = (blob: Blob, signal: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const audio = new Audio(url)
        const finish = (error?: unknown) => {
            signal.removeEventListener('abort', onAbort)
            audio.pause()
            URL.revokeObjectURL(url)
            if (error) reject(error)
            else resolve()
        }
        const onAbort = () => finish(aborted())
        signal.addEventListener('abort', onAbort)
        audio.onended = () => finish()
        audio.onerror = () => finish(new Error('Audio playback failed'))
        audio.play().catch(finish)
    })

/** The browser's own voice, as a fallback. */
const speakWithBrowser = (text: string, signal: AbortSignal) =>
    new Promise<void>((resolve) => {
        if (!browserVoice() || !text.trim()) {
            resolve()
            return
        }
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.lang = navigator.language || 'en-IN'
        utterance.onend = () => resolve()
        utterance.onerror = () => resolve()
        signal.addEventListener('abort', () => {
            window.speechSynthesis.cancel()
            resolve()
        })
        window.speechSynthesis.speak(utterance)
    })

const readAloud = async (plain: string, signal: AbortSignal) => {
    const pieces = splitForSpeech(plain)
    let index = 0
    try {
        let next = fetchAiSpeech(pieces[0], signal)
        for (; index < pieces.length; index++) {
            const blob = await next
            if (index + 1 < pieces.length) {
                next = fetchAiSpeech(pieces[index + 1], signal)
                next.catch(() => undefined) // handled when awaited
            }
            await playBlob(blob, signal)
        }
        return
    } catch (error) {
        if (signal.aborted) return
        console.info(
            'Server speech unavailable, using the browser voice:',
            error,
        )
    }
    await speakWithBrowser(pieces.slice(index).join(' '), signal)
}

export const useReadAloud = () => {
    const [speakingId, setSpeakingId] = useState<string | null>(null)
    const sessionRef = useRef<{ id: string; controller: AbortController }>()

    const stop = useCallback(() => {
        sessionRef.current?.controller.abort()
        sessionRef.current = undefined
        if (browserVoice()) window.speechSynthesis.cancel()
        setSpeakingId(null)
    }, [])

    // Stop speaking if the chat unmounts.
    useEffect(() => stop, [stop])

    /** Starts reading a reply, or stops it when it is the one being read. */
    const toggle = useCallback(
        (id: string, plain: string) => {
            const wasSpeaking = sessionRef.current?.id === id
            stop()
            if (wasSpeaking || !plain.trim()) return
            const controller = new AbortController()
            sessionRef.current = { id, controller }
            setSpeakingId(id)
            readAloud(plain, controller.signal).finally(() => {
                if (sessionRef.current?.controller === controller) {
                    sessionRef.current = undefined
                    setSpeakingId(null)
                }
            })
        },
        [stop],
    )

    return { speakingId, toggle, stop }
}
